import { useDeferredValue, useState, type FormEvent, type SyntheticEvent } from "react";
import { useForm, type FieldErrors } from "react-hook-form";

import toast from "components/partials/toast/toast";

import { useCurrentClient } from "hooks/api/use-clients";
import { useEmailTypes } from "hooks/api/use-email-types";
import { useSaveThemeSections, useThemeQuery } from "hooks/api/use-theme";
import { useUnsavedChangesGuard } from "hooks/use-unsaved-changes-guard";

import {
  formValuesToThemeSections,
  getPaletteColorOptions,
  getThemePalette,
  themeToFormValues,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.helpers";
import { buildEmailPreviewDocument } from "containers/admin/clients/client/theme-configuration-page/theme-preview/templates/email-preview-document";
import { getDefaultSender } from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.helpers";

import type {
  EmailConfigurationFormValues,
  EmailConfigurationTab,
} from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.types";
import type { ScreenSize } from "containers/admin/clients/client/theme-configuration-page/theme-preview/theme-preview.constants";

import { EMAIL_CONFIGURATION_TABS } from "containers/admin/clients/client/theme-configuration-page/configuration/email-configuration/email-configuration.constants";

/** Each tab edits the form section of the same name. */
const getTabsWithErrors = (errors: FieldErrors<EmailConfigurationFormValues>) =>
  EMAIL_CONFIGURATION_TABS.map(({ value }) => value).filter((tab) => tab in errors);

export const useEmailConfiguration = () => {
  const client = useCurrentClient();
  const { data: theme, isLoading: isThemeLoading, error: themeError } = useThemeQuery(client.id);
  // A failed request falls back to "Not defined", as the legacy preview did; only the wait matters here.
  const { data: emailTypes, isLoading: areEmailTypesLoading } = useEmailTypes(client.id);
  const saveThemeSections = useSaveThemeSections(client.id);

  const [activeTab, setActiveTab] = useState<EmailConfigurationTab>("typography");
  const [isEditing, setIsEditing] = useState(false);
  const [screenSize, setScreenSize] = useState<ScreenSize>("small");
  const [isBaseFontApplied, setIsBaseFontApplied] = useState(false);
  // Remounts the fields after a discard or save, so per-field UI state (a colour switched to Custom) resets too.
  const [fieldsKey, setFieldsKey] = useState(0);

  const form = useForm<EmailConfigurationFormValues>({
    mode: "onChange",
    defaultValues: themeToFormValues(undefined),
    values: theme ? themeToFormValues(theme) : undefined,
    // A background refetch of the theme must not wipe what is being edited.
    resetOptions: { keepDirtyValues: true },
  });

  const finishEditing = () => {
    setIsEditing(false);
    setFieldsKey((key) => key + 1);
  };

  const discardChanges = () => {
    // `reset()` inherits `resetOptions`, so the dirty values a refetch must keep are dropped explicitly here.
    form.reset(undefined, { keepDirtyValues: false });
    finishEditing();
  };

  const { handleCancelClick, unsavedChangesDialog } = useUnsavedChangesGuard({
    isEditing,
    isDirty: form.formState.isDirty,
    onDiscard: discardChanges,
  });

  const values = form.watch();
  const previewTheme = { ...theme, ...formValuesToThemeSections(values) };
  // Typing keeps its pace while the preview document, rebuilt on every change, catches up.
  const srcDoc = useDeferredValue(
    isThemeLoading || areEmailTypesLoading
      ? null
      : buildEmailPreviewDocument({
          theme: previewTheme,
          sender: getDefaultSender(emailTypes),
          clientName: client.name,
          isBaseFontApplied,
        }),
  );

  const saveValues = async (formValues: EmailConfigurationFormValues) => {
    try {
      await saveThemeSections.mutateAsync(formValuesToThemeSections(formValues));
      finishEditing();
    } catch (error) {
      console.error("Failed to save theme:", error);
      toast.error({ data: { title: "Failed to save theme" } });
    }
  };

  const showFirstInvalidTab = (errors: FieldErrors<EmailConfigurationFormValues>) => {
    const [firstInvalidTab] = getTabsWithErrors(errors);

    if (firstInvalidTab) {
      setActiveTab(firstInvalidTab);
    }
  };

  const handleSave = (event: FormEvent<HTMLFormElement>) => {
    void form.handleSubmit(saveValues, showFirstInvalidTab)(event);
  };

  const handleTabChange = (_event: SyntheticEvent, tab: EmailConfigurationTab) => setActiveTab(tab);

  const handleEdit = () => setIsEditing(true);

  return {
    form,
    activeTab,
    tabsWithErrors: getTabsWithErrors(form.formState.errors),
    isEditing,
    isEditDisabled: isThemeLoading || !!themeError,
    isSaving: saveThemeSections.isPending,
    themeError,
    fieldsKey,
    beeFonts: theme?.fonts?.beeFonts ?? [],
    colorOptions: getPaletteColorOptions(getThemePalette(theme)),
    srcDoc,
    screenSize,
    isBaseFontApplied,
    unsavedChangesDialog,
    setScreenSize,
    setIsBaseFontApplied,
    handleTabChange,
    handleEdit,
    handleCancel: handleCancelClick,
    handleSave,
  };
};
